import React, { useState } from 'react';
import PromocodeDecoder from '../../utils/promocodeDecoder';
import { PromocodeHelpers } from '../../config/promocode.js';

const PromocodeDecoderComponent = () => {
  const [promocode, setPromocode] = useState('');
  const [decodedResult, setDecodedResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDecode = () => {
    setLoading(true);
    setError('');
    setDecodedResult(null);

    if (!promocode.trim()) {
      setError('Please enter a promocode to decode');
      setLoading(false);
      return;
    }

    try {
      const decoded = PromocodeDecoder.decode(promocode.trim());
      
      if (!decoded.isValid) {
        setError(decoded.error || 'Invalid promocode format');
        setLoading(false);
        return;
      }

      setDecodedResult(decoded);
    } catch (err) {
      setError('Error decoding promocode: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setPromocode('');
    setDecodedResult(null);
    setError('');
  };

  const formatCredits = (credits) => {
    if (credits >= 1000000) {
      return `${(credits / 1000000).toFixed(1)}M`;
    } else if (credits >= 1000) {
      return `${(credits / 1000).toFixed(1)}K`;
    }
    return credits.toString();
  };

  const getPlanTypeDisplay = (planType) => {
    const planTypes = {
      'lifetime': 'Lifetime',
      'monthly': 'Monthly',
      'yearly': 'Yearly'
    };
    return planTypes[planType] || planType;
  };

  const getPlatformDisplay = (platform, platformCode) => {
    const platformConfig = PromocodeHelpers.getPlatformByCode(platformCode);
    if (platformConfig) {
      return `${platformConfig.name} (${platformCode})`;
    }
    return `${platform} (${platformCode})`;
  };

  const isExpired = (expiryYear) => {
    return expiryYear < new Date().getFullYear();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        Promocode Decoder
      </h3>
      <p className="text-sm text-gray-600 mb-6">
        Decode promocodes to view their embedded details including platform, plan type, credits, and expiry information.
      </p>

      {/* Input Section */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Enter Promocode
        </label>
        <div className="flex space-x-3">
          <input
            type="text"
            value={promocode}
            onChange={(e) => setPromocode(e.target.value)}
            placeholder="Enter promocode to decode (e.g., XYL140K2025ABC12345)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <button
            onClick={handleDecode}
            disabled={loading || !promocode.trim()}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? 'Decoding...' : 'Decode'}
          </button>
          <button
            onClick={handleClear}
            className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Decoded Result */}
      {decodedResult && (
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Decoded Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Original Code</label>
                <p className="mt-1 text-sm font-mono text-gray-900 bg-white p-2 rounded border">
                  {decodedResult.originalCode}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Platform</label>
                <p className="mt-1 text-sm text-gray-900">
                  {getPlatformDisplay(decodedResult.platform, decodedResult.platformCode)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Plan Type</label>
                <p className="mt-1 text-sm text-gray-900">
                  {getPlanTypeDisplay(decodedResult.planType)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Credits</label>
                <p className="mt-1 text-sm text-gray-900">
                  {formatCredits(decodedResult.credits)} ({decodedResult.credits.toLocaleString()})
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Expiry Year</label>
                <p className={`mt-1 text-sm ${isExpired(decodedResult.expiryYear) ? 'text-red-600' : 'text-gray-900'}`}>
                  {decodedResult.expiryYear} {isExpired(decodedResult.expiryYear) && '(Expired)'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Random Suffix</label>
                <p className="mt-1 text-sm font-mono text-gray-900">
                  {decodedResult.randomSuffix}
                </p>
              </div>
            </div>
          </div>

          {/* Code Structure Breakdown */}
          <div className="bg-blue-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Code Structure Breakdown</h4>
            {decodedResult.isStructured ? (
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Platform Code:</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {decodedResult.platformCode}
                  </span>
                  <span className="text-xs text-gray-500">
                    {(() => {
                      const platformConfig = PromocodeHelpers.getPlatformByCode(decodedResult.platformCode);
                      return platformConfig ? platformConfig.name : 'Unknown Platform';
                    })()}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Plan Type:</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {decodedResult.planType === 'lifetime' ? 'L' : 
                     decodedResult.planType === 'monthly' ? 'M' : 
                     decodedResult.planType === 'yearly' ? 'Y' : '?'}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({getPlanTypeDisplay(decodedResult.planType)})
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Credits:</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {formatCredits(decodedResult.credits)}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Expiry Year:</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {decodedResult.expiryYear}
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm font-medium text-gray-700">Random Suffix:</span>
                  <span className="text-sm font-mono bg-white px-2 py-1 rounded border">
                    {decodedResult.randomSuffix}
                  </span>
                </div>
              </div>
            ) : (
              <div className="text-sm text-gray-600">
                <p>This promocode uses default values:</p>
                <ul className="mt-2 list-disc list-inside space-y-1">
                  <li>Platform: {decodedResult.platform}</li>
                  <li>Plan Type: {getPlanTypeDisplay(decodedResult.planType)}</li>
                  <li>Credits: {formatCredits(decodedResult.credits)}</li>
                  <li>Expiry: {decodedResult.expiryYear}</li>
                </ul>
              </div>
            )}
          </div>

          {/* Credit System Integration */}
          <div className="bg-green-50 rounded-lg p-4">
            <h4 className="text-lg font-medium text-gray-900 mb-4">Credit System Integration</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">Subscription Plan</label>
                <p className="mt-1 text-sm text-gray-900">
                  {PromocodeDecoder.getCreditSystemDetails(decodedResult).subscriptionPlan}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Billing Cycle</label>
                <p className="mt-1 text-sm text-gray-900">
                  {PromocodeDecoder.getCreditSystemDetails(decodedResult).billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Reset Extension</label>
                <p className="mt-1 text-sm text-gray-900">
                  {(() => {
                    const creditDetails = PromocodeDecoder.getCreditSystemDetails(decodedResult);
                    if (creditDetails.resetExtensionYears) {
                      return `${creditDetails.resetExtensionYears} year${creditDetails.resetExtensionYears > 1 ? 's' : ''}`;
                    } else if (creditDetails.resetExtensionMonths) {
                      return `${creditDetails.resetExtensionMonths} month${creditDetails.resetExtensionMonths > 1 ? 's' : ''}`;
                    }
                    return 'N/A';
                  })()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">Status</label>
                <p className={`mt-1 text-sm ${isExpired(decodedResult.expiryYear) ? 'text-red-600' : 'text-green-600'}`}>
                  {isExpired(decodedResult.expiryYear) ? 'Expired' : 'Valid'}
                </p>
              </div>
            </div>
          </div>

          {/* Additional Flags */}
          {decodedResult.isUnknownPlatform && (
            <div className="bg-yellow-50 rounded-lg p-4">
              <h4 className="text-lg font-medium text-gray-900 mb-4">Additional Information</h4>
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                    Unknown Platform
                  </span>
                  <span className="text-sm text-gray-600">
                    Platform code '{decodedResult.platformCode}' is not recognized
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Help Section */}
      <div className="mt-8 bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-medium text-gray-900 mb-3">How to Use</h4>
        <div className="text-sm text-gray-600 space-y-2">
          <p><strong>Structured Format:</strong> [Platform][Plan][Credits][Year][Random] (e.g., XYL140K2025ABC12345)</p>
          <p><strong>Platform Codes:</strong> 2-letter codes managed dynamically</p>
          <p><strong>Plan Types:</strong> L=Lifetime, M=Monthly, Y=Yearly</p>
          <p><strong>Credits:</strong> Numbers with K/M suffixes (10K = 10,000)</p>
        </div>
      </div>
    </div>
  );
};

export default PromocodeDecoderComponent;
